export const MERCH_FULFILLMENT_LABELS = {
  awaiting_stock: 'Prenotato — in attesa di stampa',
  in_print: 'In stampa',
  paid: 'Pagato — in lavorazione',
  preparing: 'Pronto',
  shipped: 'Spedito',
  ready_pickup: 'Pronto ritiro',
  delivered: 'Consegnato',
  cancelled: 'Cancellato',
};

export const MERCH_FULFILLMENT_SHORT_LABELS = {
  awaiting_stock: 'Prenotato',
  in_print: 'In stampa',
  paid: 'In lavorazione',
  preparing: 'Pronto',
  shipped: 'Spedito',
  ready_pickup: 'Pronto ritiro',
  delivered: 'Consegnato',
  cancelled: 'Cancellato',
};

export const MERCH_FULFILLMENT_COLORS = {
  awaiting_stock: 'orange',
  in_print: 'pink',
  paid: 'yellow',
  preparing: 'blue',
  shipped: 'purple',
  ready_pickup: 'teal',
  delivered: 'green',
  cancelled: 'gray',
};

export function getMerchStock(product) {
  return product?.stockDisponibili ?? product?.quantita ?? 0;
}

export function isMerchPrenotabile(product) {
  return product?.prenotabile === true;
}

export function isMerchPreorder(product, orderQty = 1) {
  const available = getMerchStock(product);
  const qty = Math.max(1, Number(orderQty) || 1);
  return isMerchPrenotabile(product) && available < qty;
}

export function canPurchaseMerch(product, orderQty = 1) {
  if (product?.visibile !== true) return false;
  if (product.disponibilita === 'nonDisponibile') return false;

  const available = getMerchStock(product);
  const qty = Math.max(1, Number(orderQty) || 1);

  if (available >= qty) return true;
  return isMerchPrenotabile(product);
}

export function getMerchFulfillmentLabel(status, { short = false } = {}) {
  const map = short ? MERCH_FULFILLMENT_SHORT_LABELS : MERCH_FULFILLMENT_LABELS;
  return map[status] || status || '—';
}

export function getMerchFulfillmentColor(status) {
  return MERCH_FULFILLMENT_COLORS[status] || 'gray';
}

export function getPreorderMessage(fulfillmentMethod) {
  if (fulfillmentMethod === 'shipping') {
    return 'Stai effettuando una prenotazione. Controlla «I tuoi ordini» per seguire gli aggiornamenti sulla spedizione.';
  }
  return 'Stai effettuando una prenotazione. Controlla «I tuoi ordini» per seguire gli aggiornamenti sul ritiro.';
}

export function getMerchOrderConfirmationTitle({ isPreorder } = {}) {
  return isPreorder ? 'Prenotazione confermata' : 'Pagamento completato';
}

export function getMerchOrderConfirmationMessage({
  isPreorder,
  fulfillmentMethod,
} = {}) {
  if (isPreorder) {
    if (fulfillmentMethod === 'shipping') {
      return 'La tua prenotazione è stata registrata. Controlla «I tuoi ordini» per seguire gli aggiornamenti sulla spedizione.';
    }
    return 'La tua prenotazione è stata registrata. Controlla «I tuoi ordini» per seguire gli aggiornamenti sul ritiro.';
  }

  if (fulfillmentMethod === 'shipping') {
    return "Il pagamento è andato a buon fine. Controlla «I tuoi ordini» per seguire gli aggiornamenti sulla spedizione all'indirizzo indicato.";
  }

  return 'Il pagamento è andato a buon fine. Controlla «I tuoi ordini» per seguire gli aggiornamenti sul ritiro in sede o in occasione di un evento CIL.';
}

export function getMerchOrderNextSteps({ isPreorder, fulfillmentMethod } = {}) {
  if (fulfillmentMethod === 'shipping') {
    if (isPreorder) {
      return [
        'Metteremo in produzione il prodotto prenotato.',
        "Quando sarà pronto, lo spediremo all'indirizzo che hai indicato.",
        'Controlla «I tuoi ordini» per seguire gli aggiornamenti e il tracking.',
      ];
    }
    return [
      'Prepareremo il tuo ordine per la spedizione.',
      'Controlla «I tuoi ordini» per seguire gli aggiornamenti sulla spedizione.',
      'Troverai il numero di tracking non appena il pacco sarà affidato al corriere.',
    ];
  }

  if (isPreorder) {
    return [
      'Metteremo in produzione il prodotto prenotato.',
      'Controlla «I tuoi ordini» per sapere quando sarà pronto per il ritiro.',
      'Potrai ritirarlo in sede o in occasione di un evento CIL.',
    ];
  }

  return [
    'Prepareremo il tuo ordine per il ritiro.',
    'Controlla «I tuoi ordini» per sapere quando potrai passare a ritirarlo.',
    'Potrai ritirarlo in sede o in occasione di un evento CIL.',
  ];
}

export function flattenSocioRecord(socio) {
  if (!socio || typeof socio !== 'object') return null;

  let node = socio;
  if (node.data !== undefined) {
    node = Array.isArray(node.data) ? node.data[0] : node.data;
  }
  if (!node) return null;

  if (node.attributes && typeof node.attributes === 'object') {
    return {
      id: node.id,
      documentId: node.documentId,
      ...node.attributes,
    };
  }

  return node;
}

export function getMerchSocioCustomerName(socio) {
  const flat = flattenSocioRecord(socio);
  if (!flat) return '';

  if (flat.isMinorenne === true) {
    return [flat.nomeGenitore, flat.cognomeGenitore]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  return [flat.nomeSocio, flat.cognomeSocio].filter(Boolean).join(' ').trim();
}

export function resolveMerchCheckoutSocio(userData, participantId) {
  const tesseramenti = userData?.tesseramenti || [];
  if (tesseramenti.length === 0) return null;

  if (participantId) {
    const match = tesseramenti.find((socio) => {
      const flat = flattenSocioRecord(socio);
      return (
        flat?.documentId === participantId ||
        String(flat?.id) === String(participantId)
      );
    });
    if (match) return flattenSocioRecord(match);
  }

  return flattenSocioRecord(tesseramenti[0]);
}

export {
  formatMerchShippingAddress,
  getMerchShippingRecipientName,
  isMerchShippingAddressComplete,
  EMPTY_MERCH_SHIPPING_ADDRESS,
} from './merchShipping';
